import supabase from '../supabase/supabase';

import { reportService } from './reportService';
import { calculationService } from './calculationService';
import { dashboardService } from './dashboardService';
import { coaService } from './coaService';
import { transactionService } from './transactionService';
import { invoiceService } from './invoiceService';
import { bankService } from './bankService';
import { reconciliationService } from './reconciliationService';

/*
 * ============================================================
 * PROFITYO AI SERVICE
 *
 * Supports:
 * - Accounting Firm AI Copilot
 * - Client Portal AI Copilot
 *
 * SECURITY
 * - Uses the authenticated Supabase session
 * - Keeps client and firm requests separate
 * - Never treats frontend financial data as trusted AI data
 * - The Edge Function verifies authorization independently
 * - The Edge Function loads organization-scoped financial data
 *
 * Existing financial context helpers are preserved
 * for compatibility with other application components.
 * ============================================================
 */

/* ============================================================
   CONFIGURATION
============================================================ */

const MAX_HISTORY = 10;
const MAX_PROMPT_LENGTH = 4000;

const MAX_RECENT_TRANSACTIONS = 30;
const MAX_ACCOUNT_RECORDS = 100;
const MAX_BANK_RECORDS = 30;
const MAX_RECONCILIATION_RECORDS = 20;
const MAX_INVOICE_RECORDS = 20;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* ============================================================
   GENERAL HELPERS
============================================================ */

const getRows = (value) =>
  Array.isArray(value) ? value : [];

const getErrorMessage = (error, fallback) =>
  typeof error?.message === 'string' &&
  error.message.trim()
    ? error.message
    : fallback;

const toNumberOrNull = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
};

const getLocalToday = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

const getCurrentPeriod = () => {
  const today = getLocalToday();

  return {
    startDate: `${today.slice(0, 4)}-01-01`,
    endDate: today
  };
};

const getOrganizationId = (row) =>
  row?.organization_id ??
  row?.org_id ??
  row?.id ??
  null;

const getManagingFirmId = (row) =>
  row?.managing_organization_id ??
  row?.managing_org_id ??
  row?.account_id ??
  null;

const isValidOrganizationId = (id) =>
  typeof id === 'string' &&
  UUID_PATTERN.test(id);

/* ============================================================
   AUTHENTICATION
============================================================ */

const getAuthenticatedUser = async () => {
  const { data, error } =
    await supabase.auth.getUser();

  if (error || !data?.user) {
    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  return data.user;
};

const getAuthenticatedSession = async () => {
  const authenticatedUser =
    await getAuthenticatedUser();

  const { data, error } =
    await supabase.auth.getSession();

  const session = data?.session;

  if (
    error ||
    !session?.access_token ||
    !session.user?.id ||
    session.user.id !== authenticatedUser.id
  ) {
    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  return {
    user: authenticatedUser,
    accessToken: session.access_token
  };
};

/* ============================================================
   SANITIZE AI INPUT
============================================================ */

const validatePrompt = (prompt) => {
  const trimmed =
    typeof prompt === 'string'
      ? prompt.trim()
      : '';

  if (!trimmed) {
    throw new Error(
      'Please enter a question.'
    );
  }

  if (trimmed.length > MAX_PROMPT_LENGTH) {
    throw new Error(
      `Your question must be ${MAX_PROMPT_LENGTH} characters or fewer.`
    );
  }

  return trimmed;
};

const sanitizeHistory = (history) =>
  getRows(history)
    .filter(
      (message) =>
        ['user', 'assistant'].includes(
          message?.role
        ) &&
        typeof message.content === 'string'
    )
    .slice(-MAX_HISTORY)
    .map((message) => ({
      role: message.role,
      content: message.content.slice(
        0,
        MAX_PROMPT_LENGTH
      )
    }));

/* ============================================================
   EDGE FUNCTION ERROR HANDLING
============================================================ */

const extractFunctionError = async (error) => {
  let message = getErrorMessage(
    error,
    'Unable to reach ProfitYo AI.'
  );

  /*
   * Supabase FunctionsHttpError may contain
   * a JSON response with the backend error.
   */

  try {
    if (error?.context?.json) {
      const details =
        await error.context.json();

      if (
        typeof details?.error === 'string' &&
        details.error.trim()
      ) {
        message = details.error;
      }
    }
  } catch {
    // Keep the original error.
  }

  return message;
};

/* ============================================================
   COMMON VERIFIED AI REQUEST

   Every AI request:
   - Uses the actual Supabase session
   - Passes exactly one requested organization
   - Specifies firm or client mode
   - Sends no frontend-generated financial snapshot

   IMPORTANT:
   Browser-selected IDs are NOT authorization.

   The Supabase Edge Function independently verifies
   membership, firm-client relationships, and RLS.
============================================================ */

const invokeAccountingCopilot = async ({
  prompt,
  history = [],
  organizationId,
  workspaceMode,
  expectedUserId = null
}) => {
  const safePrompt =
    validatePrompt(prompt);

  if (!isValidOrganizationId(organizationId)) {
    throw new Error(
      'Select a valid organization before using ProfitYo AI.'
    );
  }

  if (
    workspaceMode !== 'firm' &&
    workspaceMode !== 'client'
  ) {
    throw new Error(
      'Invalid AI workspace mode.'
    );
  }

  const {
    user,
    accessToken
  } = await getAuthenticatedSession();

  if (
    expectedUserId &&
    expectedUserId !== user.id
  ) {
    throw new Error(
      'Your active account changed. Please try again.'
    );
  }

  const safeHistory =
    sanitizeHistory(history);

  /*
   * The backend deliberately ignores browser:
   * - userId
   * - role
   * - context
   * - financialContext
   * - calculated totals
   *
   * Only the selected organization ID,
   * request mode, question and chat are sent.
   */

  const { data, error } =
    await supabase.functions.invoke(
      'ai-accounting-copilot',
      {
        body: {
          prompt: safePrompt,
          history: safeHistory,

          workspaceMode,

          activeOrganizationId:
            organizationId
        },

        headers: {
          Authorization:
            `Bearer ${accessToken}`
        }
      }
    );

  if (error) {
    const errorMessage =
      await extractFunctionError(error);

    throw new Error(errorMessage);
  }

  if (data?.error) {
    throw new Error(
      typeof data.error === 'string'
        ? data.error
        : 'ProfitYo AI could not process your request.'
    );
  }

  const responseMessage =
    typeof data?.message === 'string'
      ? data.message.trim()
      : '';

  if (!responseMessage) {
    throw new Error(
      'ProfitYo AI returned an empty response. Please try again.'
    );
  }

  return {
    ...data,
    message: responseMessage
  };
};

/* ============================================================
   LEGACY FIRM CLIENT VERIFICATION

   Used by existing financial context helpers.

   The client hub is a list of potentially accessible
   clients; the server must enforce authorization again.

   For the AI chat itself, the Edge Function provides
   the authoritative access decision.
============================================================ */

const verifyAuthorizedClient = async (
  activeClient
) => {
  const requestedId =
    activeClient?.id;

  if (!isValidOrganizationId(requestedId)) {
    throw new Error(
      'Please select a valid client organization.'
    );
  }

  const user =
    await getAuthenticatedUser();

  const [
    hubResult,
    workspaceResult
  ] = await Promise.all([
    supabase.rpc(
      'get_client_hub_organizations_v2'
    ),

    supabase.rpc(
      'get_my_workspaces'
    )
  ]);

  if (hubResult.error) {
    throw new Error(
      `Unable to verify client access: ${hubResult.error.message}`
    );
  }

  if (workspaceResult.error) {
    throw new Error(
      `Unable to verify firm membership: ${workspaceResult.error.message}`
    );
  }

  const ownerFirmIds = new Set(
    getRows(workspaceResult.data)
      .filter(
        (workspace) =>
          workspace.workspace_type === 'firm' &&
          [
            'accounting_firm',
            'accountant_practice'
          ].includes(
            workspace.organization_type
          ) &&
          workspace.membership_role === 'owner'
      )
      .map((workspace) =>
        workspace.organization_id
      )
  );

  const authorizedClient =
    getRows(hubResult.data).find(
      (row) =>
        getOrganizationId(row) === requestedId &&
        row.is_client === true &&
        row.type === 'client' &&
        ownerFirmIds.has(
          getManagingFirmId(row)
        )
    );

  if (!authorizedClient) {
    throw new Error(
      'This client is not managed by one of your authorized accounting firms.'
    );
  }

  /*
   * Existing dashboard services may use their own
   * scope resolver. The Edge Function does NOT trust
   * the result of this frontend helper.
   */

  const dashboardScope =
    await dashboardService.getClientDashboardScope(
      requestedId
    );

  const financialAccountId =
    dashboardScope?.financial_account_id ||
    dashboardScope?.organization_id;

  if (!financialAccountId) {
    throw new Error(
      'The selected client does not have a valid accounting scope.'
    );
  }

  return {
    user,
    client: authorizedClient,

    organizationId: requestedId,
    financialAccountId,

    managingFirmId:
      getManagingFirmId(authorizedClient)
  };
};

/* ============================================================
   FINANCIAL CONTEXT RESULT HANDLING

   Existing helper methods are preserved.

   Failed queries are labeled unavailable,
   not converted into financial zeros.
============================================================ */

const unwrapResult = (
  settledResult,
  label,
  expectArray = false
) => {
  if (settledResult.status === 'rejected') {
    return {
      available: false,
      data: null,
      reason:
        settledResult.reason?.message ||
        `${label} could not be loaded.`
    };
  }

  const value =
    settledResult.value;

  if (
    value &&
    typeof value === 'object' &&
    'error' in value &&
    value.error
  ) {
    return {
      available: false,
      data: null,
      reason:
        value.error.message ||
        `${label} could not be loaded.`
    };
  }

  const normalized =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'data' in value &&
    !('netIncome' in value)
      ? value.data
      : value;

  if (
    normalized === null ||
    normalized === undefined
  ) {
    return {
      available: false,
      data: null,
      reason:
        `${label} returned no data.`
    };
  }

  if (
    expectArray &&
    !Array.isArray(normalized)
  ) {
    return {
      available: false,
      data: null,
      reason:
        `${label} returned an unexpected format.`
    };
  }

  return {
    available: true,
    data: normalized,
    reason: null
  };
};

/* ============================================================
   REPORT MAPPING
============================================================ */

const buildProfitLoss = (result) => {
  if (!result.available) return null;

  const pl = result.data;

  return {
    revenue:
      toNumberOrNull(pl?.income?.total),

    expenses:
      toNumberOrNull(pl?.expenses?.total),

    grossProfit:
      toNumberOrNull(pl?.grossProfit),

    netIncome:
      toNumberOrNull(pl?.netIncome),

    expenseAccounts:
      pl?.expenses?.accounts &&
      typeof pl.expenses.accounts === 'object'
        ? pl.expenses.accounts
        : null
  };
};

const buildAccountsReceivable = (result) => {
  if (!result.available) return null;

  const aging = result.data;

  const buckets =
    aging?.grandTotals?.buckets || null;

  const total =
    toNumberOrNull(
      aging?.grandTotals?.total
    );

  const current =
    toNumberOrNull(
      buckets?.current
    );

  return {
    total,
    buckets,

    overdueAmount:
      total !== null &&
      current !== null
        ? Math.max(0, total - current)
        : null,

    overdueInvoiceCount: null
  };
};

const buildInvoiceOverview = (
  result,
  today
) => {
  if (!result.available) return null;

  const invoices =
    getRows(result.data);

  const excludedStatuses = new Set([
    'paid',
    'draft',
    'void',
    'cancelled',
    'canceled'
  ]);

  const overdue = invoices.filter(
    (invoice) => {
      const status = String(
        invoice.status || ''
      ).toLowerCase();

      const dueDate = String(
        invoice.due_date || ''
      ).slice(0, 10);

      return (
        !excludedStatuses.has(status) &&
        /^\d{4}-\d{2}-\d{2}$/.test(dueDate) &&
        dueDate < today
      );
    }
  );

  return {
    loadedInvoiceCount:
      invoices.length,

    overdueLoadedInvoiceCount:
      overdue.length,

    recent:
      invoices
        .slice(0, MAX_INVOICE_RECORDS)
        .map((invoice) => ({
          id: invoice.id,

          number:
            invoice.invoice_number ??
            invoice.number ??
            null,

          status:
            invoice.status ?? null,

          dueDate:
            invoice.due_date ?? null,

          total:
            toNumberOrNull(
              invoice.total_amount ??
              invoice.total
            )
        }))
  };
};

/* ============================================================
   STRUCTURED FINANCIAL CONTEXT

   Preserved for backwards compatibility.

   NOT transmitted to the AI Edge Function.
============================================================ */

const loadStructuredFinancialContext = async (
  verifiedScope
) => {
  const {
    organizationId,
    financialAccountId
  } = verifiedScope;

  const period =
    getCurrentPeriod();

  const {
    startDate,
    endDate
  } = period;

  const requests = [
    {
      key: 'profitLoss',
      label: 'Profit & Loss',
      run: () =>
        reportService.generatePL(
          startDate,
          endDate,
          financialAccountId
        )
    },
    {
      key: 'balanceSheet',
      label: 'Balance Sheet',
      run: () =>
        reportService.generateBalanceSheet(
          endDate,
          financialAccountId
        )
    },
    {
      key: 'cashFlow',
      label: 'Cash Flow',
      run: () =>
        reportService.generateCashFlow(
          startDate,
          endDate,
          financialAccountId
        )
    },
    {
      key: 'accountsReceivable',
      label: 'A/R Aging',
      run: () =>
        reportService.generateARAging(
          endDate,
          financialAccountId
        )
    },
    {
      key: 'accounts',
      label: 'Chart of Accounts',
      expectArray: true,
      run: () =>
        coaService.getChartOfAccounts(
          financialAccountId
        )
    },
    {
      key: 'transactions',
      label: 'Transactions',
      expectArray: true,
      run: () =>
        transactionService.getTransactions(
          {},
          financialAccountId
        )
    },
    {
      key: 'bankAccounts',
      label: 'Bank Accounts',
      expectArray: true,
      run: () =>
        bankService.getConnectedBanks(
          financialAccountId
        )
    },
    {
      key: 'reconciliations',
      label: 'Reconciliations',
      expectArray: true,
      run: () =>
        reconciliationService.getReconciliations(
          {},
          financialAccountId
        )
    },
    {
      key: 'invoices',
      label: 'Invoices',
      expectArray: true,
      run: () =>
        invoiceService.getInvoices(
          financialAccountId
        )
    },
    {
      key: 'calculations',
      label: 'Calculations',
      run: () =>
        calculationService.getFinancialCalculations(
          financialAccountId,
          startDate,
          endDate
        )
    }
  ];

  const settled =
    await Promise.allSettled(
      requests.map(
        (request) => request.run()
      )
    );

  const results = {};

  requests.forEach(
    (request, index) => {
      results[request.key] =
        unwrapResult(
          settled[index],
          request.label,
          request.expectArray || false
        );
    }
  );

  const availability =
    Object.fromEntries(
      Object.entries(results).map(
        ([key, value]) => [
          key,
          value.available
        ]
      )
    );

  const unavailableSections =
    Object.entries(results)
      .filter(
        ([, result]) =>
          !result.available
      )
      .map(
        ([key, result]) => ({
          section: key,
          reason: result.reason
        })
      );

  const availableSectionCount =
    Object.values(results).filter(
      (result) => result.available
    ).length;

  if (availableSectionCount === 0) {
    return {
      available: false,
      organizationId,
      financialAccountId,
      period,
      availability,
      unavailableSections,

      reason:
        'The client financial data could not be loaded.'
    };
  }

  const pl =
    buildProfitLoss(
      results.profitLoss
    );

  const accountsReceivable =
    buildAccountsReceivable(
      results.accountsReceivable
    );

  const invoiceOverview =
    buildInvoiceOverview(
      results.invoices,
      endDate
    );

  /* ---------------------------------------------------------
     ACCOUNTS
  --------------------------------------------------------- */

  const accountRows =
    results.accounts.available
      ? getRows(
          results.accounts.data
        )
      : null;

  const accounts = accountRows
    ? {
        loadedCount:
          accountRows.length,

        records:
          accountRows
            .slice(
              0,
              MAX_ACCOUNT_RECORDS
            )
            .map((account) => ({
              id:
                account.id,

              code:
                account.code ?? null,

              name:
                account.name ?? null,

              type:
                account.type ?? null,

              subType:
                account.sub_type ?? null,

              isActive:
                account.is_active ?? null
            })),

        recordsTruncated:
          accountRows.length >
          MAX_ACCOUNT_RECORDS
      }
    : null;

  /* ---------------------------------------------------------
     TRANSACTIONS

     Corrected:
     account_id replaces coa_id.
  --------------------------------------------------------- */

  const transactionRows =
    results.transactions.available
      ? getRows(
          results.transactions.data
        )
      : null;

  const sortedTransactions =
    transactionRows
      ? [...transactionRows].sort(
          (a, b) =>
            String(
              b.date || ''
            ).localeCompare(
              String(
                a.date || ''
              )
            )
        )
      : null;

  const transactions =
    sortedTransactions
      ? {
          loadedCount:
            sortedTransactions.length,

          recent:
            sortedTransactions
              .slice(
                0,
                MAX_RECENT_TRANSACTIONS
              )
              .map((transaction) => ({
                id:
                  transaction.id,

                date:
                  transaction.date ?? null,

                description:
                  transaction.description ?? null,

                debit:
                  toNumberOrNull(
                    transaction.debit
                  ),

                credit:
                  toNumberOrNull(
                    transaction.credit
                  ),

                status:
                  transaction.status ?? null,

                accountId:
                  transaction.account_id ?? null,

                batchId:
                  transaction.batch_id ?? null
              })),

          recordsTruncated:
            sortedTransactions.length >
            MAX_RECENT_TRANSACTIONS
        }
      : null;

  /* ---------------------------------------------------------
     UNCATEGORIZED TRANSACTIONS
  --------------------------------------------------------- */

  const accountById = new Map(
    (accountRows || []).map(
      (account) => [
        String(account.id),
        account
      ]
    )
  );

  const uncategorizedRows =
    sortedTransactions
      ? sortedTransactions.filter(
          (transaction) => {
            const account =
              accountById.get(
                String(
                  transaction.account_id ??
                  ''
                )
              );

            const name =
              String(
                account?.name || ''
              )
                .trim()
                .toLowerCase();

            return (
              name ===
                'uncategorized expense' ||
              name ===
                'uncategorized income'
            );
          }
        )
      : null;

  const uncategorizedTransactions =
    uncategorizedRows
      ? {
          loadedCount:
            uncategorizedRows.length,

          records:
            uncategorizedRows
              .slice(0, 20)
              .map(
                (transaction) => ({
                  id:
                    transaction.id,

                  date:
                    transaction.date ?? null,

                  description:
                    transaction.description ?? null,

                  debit:
                    toNumberOrNull(
                      transaction.debit
                    ),

                  credit:
                    toNumberOrNull(
                      transaction.credit
                    )
                })
              )
        }
      : null;

  /* ---------------------------------------------------------
     BANK ACCOUNTS
  --------------------------------------------------------- */

  const bankRows =
    results.bankAccounts.available
      ? getRows(
          results.bankAccounts.data
        )
      : null;

  const bankAccounts =
    bankRows
      ? {
          loadedCount:
            bankRows.length,

          records:
            bankRows
              .slice(
                0,
                MAX_BANK_RECORDS
              )
              .map((bank) => ({
                id:
                  bank.id,

                institutionName:
                  bank.institution_name ??
                  bank.institution ??
                  null,

                accountName:
                  bank.account_name ?? null,

                accountType:
                  bank.account_type ?? null,

                balance:
                  toNumberOrNull(
                    bank.balance
                  ),

                isActive:
                  bank.is_active ?? null,

                lastSyncAt:
                  bank.last_sync_at ?? null
              }))
        }
      : null;

  /* ---------------------------------------------------------
     RECONCILIATIONS
  --------------------------------------------------------- */

  const reconciliationRows =
    results.reconciliations.available
      ? getRows(
          results.reconciliations.data
        )
      : null;

  const reconciliations =
    reconciliationRows
      ? {
          loadedCount:
            reconciliationRows.length,

          records:
            [...reconciliationRows]
              .sort(
                (a, b) =>
                  String(
                    b.statement_date || ''
                  ).localeCompare(
                    String(
                      a.statement_date || ''
                    )
                  )
              )
              .slice(
                0,
                MAX_RECONCILIATION_RECORDS
              )
              .map((item) => ({
                id:
                  item.id,

                statementDate:
                  item.statement_date ??
                  null,

                status:
                  item.status ?? null,

                startingBalance:
                  toNumberOrNull(
                    item.starting_balance
                  ),

                endingBalance:
                  toNumberOrNull(
                    item.ending_balance
                  ),

                bookBalance:
                  toNumberOrNull(
                    item.book_balance
                  )
              }))
        }
      : null;

  /* ---------------------------------------------------------
     FINAL STRUCTURED CONTEXT
  --------------------------------------------------------- */

  return {
    available: true,

    organizationId,
    financialAccountId,
    period,
    availability,
    unavailableSections,

    revenue: pl
      ? {
          total:
            pl.revenue
        }
      : null,

    expenses: pl
      ? {
          total:
            pl.expenses,

          accounts:
            pl.expenseAccounts
        }
      : null,

    profitLoss: pl
      ? {
          netIncome:
            pl.netIncome,

          grossProfit:
            pl.grossProfit,

          revenue:
            pl.revenue,

          expenses:
            pl.expenses
        }
      : null,

    balanceSheet:
      results.balanceSheet.available
        ? results.balanceSheet.data
        : null,

    cashFlow:
      results.cashFlow.available
        ? results.cashFlow.data
        : null,

    invoices: {
      accountsReceivable,
      overview: invoiceOverview
    },

    transactions,
    accounts,
    bankAccounts,
    reconciliations,
    uncategorizedTransactions,

    calculations:
      results.calculations.available
        ? results.calculations.data
        : null,

    bills: null,
    customers: null,
    vendors: null
  };
};

/* ============================================================
   PUBLIC AI SERVICE
============================================================ */

export const aiService = {

  /* ---------------------------------------------------------
     EXISTING STRUCTURED FINANCIAL CONTEXT
  --------------------------------------------------------- */

  async getStructuredFinancialContext(
    activeClient = null
  ) {
    if (!activeClient?.id) {
      return {
        available: false,
        reason:
          'No active client organization is selected.'
      };
    }

    const verifiedScope =
      await verifyAuthorizedClient(
        activeClient
      );

    return loadStructuredFinancialContext(
      verifiedScope
    );
  },

  /* ---------------------------------------------------------
     EXISTING TEXT FINANCIAL CONTEXT
  --------------------------------------------------------- */

  async getFinancialContext(
    activeClient = null,
    existingStructuredContext = null
  ) {
    const financial =
      existingStructuredContext ||
      await this.getStructuredFinancialContext(
        activeClient
      );

    if (!financial.available) {
      return (
        financial.reason ||
        'Financial context is unavailable.'
      );
    }

    const display = (value) =>
      value === null ||
      value === undefined
        ? 'Unavailable'
        : String(value);

    const pl =
      financial.profitLoss;

    const ar =
      financial.invoices
        ?.accountsReceivable;

    return [
      'CURRENT CLIENT FINANCIAL SNAPSHOT',

      `Period: ${financial.period.startDate} to ${financial.period.endDate}`,

      `Revenue: ${display(pl?.revenue)}`,

      `Expenses: ${display(pl?.expenses)}`,

      `Net income: ${display(pl?.netIncome)}`,

      `Gross profit: ${display(pl?.grossProfit)}`,

      `Accounts receivable: ${display(ar?.total)}`,

      `Overdue A/R amount: ${display(ar?.overdueAmount)}`,

      `Overdue invoice count: ${display(
        financial.invoices?.overview
          ?.overdueLoadedInvoiceCount
      )}`,

      '',

      'Unavailable sections:',

      getRows(
        financial.unavailableSections
      ).length
        ? financial.unavailableSections
            .map(
              (section) =>
                section.section
            )
            .join(', ')
        : 'None reported',

      '',

      'Important: Unavailable is not the same as zero.'
    ].join('\n');
  },

  /* ---------------------------------------------------------
     EXISTING REQUEST CONTEXT

     Retained for other components that may
     call this method.

     AI Edge Function ignores this browser context.
  --------------------------------------------------------- */

  async getRequestContext(
    user,
    profile,
    activeClient
  ) {
    const verifiedScope =
      await verifyAuthorizedClient(
        activeClient
      );

    if (
      user?.id &&
      user.id !== verifiedScope.user.id
    ) {
      throw new Error(
        'The authenticated user changed. Please retry.'
      );
    }

    const {
      organizationId,
      financialAccountId,
      managingFirmId
    } = verifiedScope;

    const {
      data: organization,
      error: organizationError
    } = await supabase
      .schema('accounting')
      .from('organizations')
      .select(
        'id,name,type,created_by'
      )
      .eq(
        'id',
        organizationId
      )
      .maybeSingle();

    if (
      organizationError ||
      !organization ||
      organization.type !== 'client'
    ) {
      throw new Error(
        'The client organization could not be verified.'
      );
    }

    const structuredFinancialContext =
      await loadStructuredFinancialContext(
        verifiedScope
      );

    const financialContext =
      await this.getFinancialContext(
        activeClient,
        structuredFinancialContext
      );

    const membershipRole =
      verifiedScope.client
        .membership_role ?? null;

    const firstName =
      profile?.id === verifiedScope.user.id
        ? profile.first_name || ''
        : '';

    const lastName =
      profile?.id === verifiedScope.user.id
        ? profile.last_name || ''
        : '';

    const availableData =
      structuredFinancialContext.available
        ? structuredFinancialContext
        : null;

    const context = {
      user: {
        id:
          verifiedScope.user.id,

        firstName,
        lastName,

        name:
          [firstName, lastName]
            .filter(Boolean)
            .join(' ') || null,

        email:
          verifiedScope.user.email ||
          null,

        role:
          membershipRole
      },

      organization: {
        id:
          organization.id,

        name:
          organization.name,

        type:
          organization.type
      },

      workspace: {
        activeOrganizationId:
          organization.id,

        financialAccountId,

        activeClientName:
          organization.name,

        managingFirmId,

        organizationOwner: null,
        parentFirm: null
      },

      availableData: {
        revenue:
          availableData?.revenue ?? null,

        expenses:
          availableData?.expenses ?? null,

        profitLoss:
          availableData?.profitLoss ?? null,

        balanceSheet:
          availableData?.balanceSheet ?? null,

        cashFlow:
          availableData?.cashFlow ?? null,

        transactions:
          availableData?.transactions ?? null,

        invoices:
          availableData?.invoices ?? null,

        bills: null,
        customers: null,
        vendors: null,

        accounts:
          availableData?.accounts ?? null,

        bankAccounts:
          availableData?.bankAccounts ?? null,

        reconciliations:
          availableData?.reconciliations ?? null,

        uncategorizedTransactions:
          availableData?.uncategorizedTransactions ??
          null,

        calculations:
          availableData?.calculations ?? null,

        profitCalculations:
          availableData?.calculations
            ?.profitCalculation ?? null
      },

      financialContext,

      dataAvailability: {
        available:
          structuredFinancialContext.available,

        sections:
          structuredFinancialContext.availability ??
          {},

        unavailable:
          structuredFinancialContext.unavailableSections ??
          [],

        reason:
          structuredFinancialContext.reason ??
          null
      }
    };

    return {
      context,
      structuredFinancialContext
    };
  },

  /* =========================================================
     ACCOUNTING FIRM AI COPILOT

     Preserves your existing Assistant.jsx signature:

     askAssistant(
       prompt,
       history,
       user,
       profile,
       activeClient
     )

     Firm authorization is checked by the Edge Function.
     Only the selected managed-client ID is transmitted.
  ========================================================= */

  async askAssistant(
    prompt,
    history = [],
    user = null,
    profile = null,
    activeClient = null
  ) {
    /*
     * profile is intentionally not sent.
     *
     * The Edge Function derives the user's
     * membership from Supabase.
     */

    void profile;

    if (!activeClient?.id) {
      throw new Error(
        'Please select a client before using ProfitYo AI.'
      );
    }

    return invokeAccountingCopilot({
      prompt,

      history,

      organizationId:
        activeClient.id,

      workspaceMode:
        'firm',

      expectedUserId:
        user?.id || null
    });
  },

  /* =========================================================
     CLIENT PORTAL AI COPILOT

     Client-specific entry point:

     askClientAssistant(
       prompt,
       history,
       activeWorkspace
     )

     The selected workspace must be a client.

     The Edge Function verifies that the
     authenticated user is a direct member
     of the requested client organization.
  ========================================================= */

  async askClientAssistant(
    prompt,
    history = [],
    activeWorkspace = null
  ) {
    if (
      !activeWorkspace?.id ||
      activeWorkspace.workspaceType !== 'client'
    ) {
      throw new Error(
        'Select your authorized client workspace before using ProfitYo AI.'
      );
    }

    return invokeAccountingCopilot({
      prompt,

      history,

      organizationId:
        activeWorkspace.id,

      workspaceMode:
        'client'
    });
  }
};